import { useMutation, useQuery } from "@tanstack/react-query";
import Api from "../../features/one-query/services/ApiServices";
import { useCallback, useEffect, useState } from "react";
import { useAppDispatch } from "../hooks";
import {
  setAppSuiteCosts,
  setAvailablePackages,
  setPackageCredits,
  setPaymentCost,
  setPaymentLink,
  setPopularQuestionsAnswers,
  setSubscriptionStatus,
  setUserCredits,
} from "../account/userSlice";
import { PaymentLinkResponse } from "../../Interfaces";
import { Dayjs } from "dayjs";

interface CreatePromoCodeData {
  code: string;
  codeName: string;
  description?: string;
  promotionType: number;
  percentOff?: number;
  amountOff?: number;
  durationInmonths?: number;
  duration: number;
  expirationDate?: string;
  customerEmail?: string;
  usageLimit?: number;
  subscriptionType: number;
}

interface GetAllPromoCodesData {
  code?: string;
  status?: number;
  created?: Dayjs | undefined;
  promotionType?: number;
}

interface UpdatePromoCodeData {
  promoCodeId: string;
  codeName: string;
  description: string;
}

const getPaymentLink = async () => {
  try {
    const response = await Api.post("/Stripe/subscribe");
    return response.data;
  } catch (error) {
    throw error;
  }
};

const useGetPaymentLinkQuery = () => {
  return useQuery<PaymentLinkResponse, Error>({
    queryKey: ["paymentLink"],
    queryFn: getPaymentLink,
    enabled: false, // Disable automatic query execution
  });
};

const useGetPaymentLink = () => {
  const { error, isPending, refetch } = useGetPaymentLinkQuery();
  const dispatch = useAppDispatch();

  const getSubscribeLink = async () => {
    const { data } = await refetch();
    if (data) {
      dispatch(setPaymentLink(data.url));
    }
  };

  return { getSubscribeLink, isPending, error };
};


const cancelMySubscription = async () => {
  try {
    const response = await Api.post("/Stripe/CancelSubscription");
    return response.data;
  } catch (error) {}
};

const useCancelMySubscriptionQuery = () => {
  return useQuery({
    queryKey: ["cancelMySubscription"],
    queryFn: cancelMySubscription,
    enabled: false,
  });
};

const useCancelMySubscription = () => {
  const { error, isLoading, refetch } = useCancelMySubscriptionQuery();
  const [cancel, setCancel] = useState<boolean>();
  const cancelSubscription = async () => {
    const { data } = await refetch();
    setCancel(data);
  };

  return { cancelSubscription, isLoading, error, cancel };
};

const manageMySubscription = async () => {
  const response = await Api.get("/Stripe/ManageSubscription");
  return response.data;
};

const useManageMySubscription = () => {
  return useQuery({
    queryKey: ["manageMySubscription"],
    queryFn: manageMySubscription,
    enabled: false,
  });
};

//Get Current Subscription Status Endpoint

const getCurrentSubscription = async () => {
  const response = await Api.post("/Stripe/GetCurrentSubscriptionStatus");
  return response.data;
};

const useGetCurrentSubscription = () => {
  const dispatch = useAppDispatch();
  return useMutation({
    mutationKey: ["CurrentSubscriptionStatus"],
    mutationFn: getCurrentSubscription,
    onSuccess: (data) => {
      dispatch(setSubscriptionStatus(data));
    },
  });
};

//Get all promo codes endpoint

const getAllPromoCodes = async (params?: GetAllPromoCodesData) => {
  const queryParams = new URLSearchParams();

  // Check for 0 or any other valid value explicitly
  if (params?.code !== undefined && params?.code !== null)
    queryParams.append("code", String(params.code));
  if (params?.status !== undefined && params?.status !== null)
    queryParams.append("status", String(params.status));
  if (params?.created !== undefined && params?.created !== null)
    queryParams.append("created", params.created.format('YYYY-MM-DD'));
  if (params?.promotionType !== undefined && params?.promotionType !== null)
    queryParams.append("promotionType", String(params.promotionType));

  const queryString = queryParams.toString();
  const url = `/PromoCode/GetAllPromoCodes?${queryString}`;

  const response = await Api.get(url);
  return response.data;
};

const useGetAllPromoCodes = () => {
  return useMutation({
    mutationKey: ["GetAllPromoCodes"],
    mutationFn: getAllPromoCodes,
  });
};

//Create promoCode endpoint

const createPromoCode = async (promoCodeData: CreatePromoCodeData) => {
  const { usageLimit, ...rest } = promoCodeData;
  const dataToSend = usageLimit === 0 ? rest : promoCodeData;
  const response = await Api.post("/PromoCode/CreatePromoCode", dataToSend);
  return response.data;
};

const useCreatePromoCode = () => {
  return useMutation({
    mutationKey: ["createPromoCode"],
    mutationFn: createPromoCode,
  });
};

//Delete promoCode endpoint
const deletePromoCode = async (promoCodeId: string) => {
  const response = await Api.delete(
    `PromoCode/DeletePromoCode?promoCodeId=${promoCodeId}`
  );
  return response.data;
};

const useDeletePromoCode = () => {
  return useMutation({
    mutationKey: ["deletePromoCode"],
    mutationFn: deletePromoCode,
  });
};

//Get  promoCode by id endpoint
const getPromoCodeByID = async (promoCodeId: string) => {
  const response = await Api.get(
    `PromoCode/GetPromoCodeById?PromoCodeId=${promoCodeId}`
  );
  return response.data;
};

const useGetPromoCodeByID = () => {
  return useMutation({
    mutationKey: ["getPromoCodeByID"],
    mutationFn: getPromoCodeByID,
  });
};

//Update  promoCode endpoint
const updatePromoCode = async (data: UpdatePromoCodeData) => {
  const response = await Api.patch(
    `Stripe/UpdatePromoCode?promoCodeId=${data.promoCodeId}&codeName=${data.codeName}&description=${data.description}`
  );
  return response.data;
};

const useUpdatePromoCode = () => {
  return useMutation({
    mutationKey: ["updatePromoCode"],
    mutationFn: updatePromoCode,
  });
};

//Generate promoCode id endpoint

const generatePromoCode = async () => {
  const response = await Api.get("/PromoCode/GeneratePromocodeId");
  return response.data;
};

const useGeneratePromoCode = () => { 
  return useQuery({
    queryKey: ["generatePromoCode"],
    queryFn: generatePromoCode,
    enabled: false,
  });
};
 //////////////////////////////////get availabl packages
 const getPaymentPackages = async () => {
  try {
    const response = await Api.get("/Stripe/GetAvailablePackages");
    return response.data;
  } catch (error) {
    throw error;
  }
};

const useGetPackages = () => {
  return useQuery({
    queryKey: ["paymentPackages"],
    queryFn: getPaymentPackages,
    enabled: false, // Disable automatic query execution
  });
};

const useGetAvailablePackages = () => {
  const { error, isPending, refetch } = useGetPackages();
  const dispatch = useAppDispatch();

  const getSubscribePackages = async () => {
    const { data } = await refetch();
    if (data) {
      dispatch(setAvailablePackages(data));
    }
  };

  return {getSubscribePackages, isPending, error };
};
//////////////////////////////////Famous questions and answers
const getFamousQuestionsAndAnswers = async () => {
  try {
    const response = await Api.get("/Stripe/FAQ");
    return response.data;
  } catch (error) {
    throw error;
  }
};

const useGetFQA = () => {
  return useQuery({
    queryKey: ["FQA"],
    queryFn: getFamousQuestionsAndAnswers,
    enabled: false, // Disable automatic query execution
  });
};

const usePopularQuestionsAnswers = () => {
  const { error, isPending, refetch } = useGetFQA();
  const dispatch = useAppDispatch();

  const getFQuestionsAndAnswers = async () => {
    const { data } = await refetch();
    if (data) {
      dispatch(setPopularQuestionsAnswers(data));
    }
  };

  return {getFQuestionsAndAnswers, isPending, error };
};

//// get credits packeges////////////////
const getCreditsPackages = async () => {
  try {
    const response = await Api.get("/Credits/GetPackages");
    return response.data;
  } catch (error) {
    throw error;
  }
};

const useGetCreditPackages = () => {
  return useQuery({
    queryKey: ["GetCreditsPackages"],
    queryFn: getCreditsPackages,
    enabled:false
  });
};

/////////////////Get User Credits////////////////
const getUserCredits = async () => {
  try {
    const response = await Api.get("/Credits/GetUserCredits");
    return response.data;
  } catch (error) {
    throw error;
  }
};

const usegetUserCredits = () => {
  return useQuery({
    queryKey: ["Get User Credits"],
    queryFn: getUserCredits,
    enabled: false, 
  });
};

const useGetUserCredits = () => {
  const {refetch } = usegetUserCredits();
  const dispatch = useAppDispatch();

  const getCredits = async () => {
    const { data } = await refetch();
    if (data) {
      dispatch(setUserCredits(data));
    }
  };

  return getCredits;
};
//////////////////App suite costs
const getAppSuiteCosts = async () => {
  try {
    const response = await Api.get("/Credits/GetAppSuiteCosts");
    return response.data;
  } catch (error) {
    throw error;
  }
};

const usegetSuiteCosts= () => {
  return useQuery({
    queryKey: ["Get App Suite Costs"],
    queryFn: getAppSuiteCosts,
    enabled: false, 
  });
};

const useGetAppSuiteCosts = () => {
  const {refetch } = usegetSuiteCosts();
  const dispatch = useAppDispatch();

  const getSuiteCosts = async () => {
    const { data } = await refetch();
    if (data) {
      dispatch(setAppSuiteCosts(data))
    }
  };

  return getSuiteCosts;
};
export {
  useGetPaymentLink,
  useCancelMySubscription,
  useManageMySubscription,
  useGetCurrentSubscription,
  useCreatePromoCode,
  useGetAllPromoCodes,
  useGeneratePromoCode,
  useDeletePromoCode,
  useGetPromoCodeByID,
  useUpdatePromoCode,
  useGetAvailablePackages,
  usePopularQuestionsAnswers,
  useGetAppSuiteCosts,
  useGetCreditPackages,
  useGetUserCredits
};
